import { useState } from 'react';
import { useProfile } from '../hooks/useProfile.js';

const sections = ['Profile', 'Payments', 'Subscription', 'Integrations', 'Skills'];

const resumeOptions = [
  { value: 'original', label: 'Original', description: 'Use resume as-is.' },
  { value: 'standard', label: 'Standard', description: 'Reorder and highlight relevant experience.' },
  { value: 'tailored', label: 'Tailored', description: 'Rewrite to match job description, factual only.' },
];

const coverLetterOptions = [
  { value: 'off', label: 'Off', description: "Don't generate." },
  { value: 'standard', label: 'Standard', description: 'Tailor tone, no fabrication.' },
  { value: 'tailored', label: 'Tailored', description: 'Rewrite to match JD.' },
];

const notificationDefaults = {
  whatsapp: true,
  email: true,
  application: true,
};

export default function SettingsPage() {
  const { profile, user, loading } = useProfile();
  const [activeSection, setActiveSection] = useState('Profile');
  const [resumeMode, setResumeMode] = useState(profile?.resume_optimization_mode || 'standard');
  const [coverLetterMode, setCoverLetterMode] = useState('standard');
  const [notifications, setNotifications] = useState({
    ...notificationDefaults,
    whatsapp: profile?.notifications_whatsapp ?? true,
    application: profile?.push_notifications ?? false,
  });

  const toggleNotification = (key) => {
    setNotifications((current) => ({ ...current, [key]: !current[key] }));
  };

  if (loading) return <SettingsSkeleton />;

  return (
    <section className="space-y-6">
      <SettingsHeader />

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <nav className="sticky top-6 rounded-xl bg-white p-2 shadow-sm shadow-slate-900/5" aria-label="Settings sections">
            {sections.map((section) => (
              <button
                key={section}
                type="button"
                onClick={() => setActiveSection(section)}
                className={[
                  'flex h-11 w-full cursor-pointer items-center rounded-[8px] px-3 text-left text-sm font-bold transition focus:outline-none focus:ring-4 focus:ring-teal-100',
                  activeSection === section ? 'bg-obsidian-400 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800',
                ].join(' ')}
              >
                {section}
              </button>
            ))}
          </nav>
        </aside>

        <main className="space-y-6">
          <MobileSectionNav activeSection={activeSection} onChange={setActiveSection} />
          <ProfileSection profile={profile} user={user} />
          <PaymentsSection profile={profile} />
          <SubscriptionSection profile={profile} />
          <IntegrationsSection />
          <SkillsSection profile={profile} />
          <PreferencesSection
            resumeMode={resumeMode}
            onResumeModeChange={setResumeMode}
            coverLetterMode={coverLetterMode}
            onCoverLetterModeChange={setCoverLetterMode}
            notifications={notifications}
            onToggleNotification={toggleNotification}
          />
          <DangerZone />
          <div className="flex justify-end">
            <button type="button" className="h-11 w-full cursor-pointer rounded-[8px] bg-teal-600 px-5 text-sm font-bold text-white shadow-lg shadow-teal-900/10 transition hover:-translate-y-0.5 hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100 sm:w-auto">
              Save settings
            </button>
          </div>
        </main>
      </div>
    </section>
  );
}

function SettingsHeader() {
  return (
    <header className="rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 p-6 text-white shadow-md">
      <h1 className="font-display text-3xl text-white">Settings</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/80">Manage your account and preferences</p>
    </header>
  );
}

function MobileSectionNav({ activeSection, onChange }) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 lg:hidden" role="tablist" aria-label="Settings sections">
      <div className="flex min-w-max gap-2 rounded-xl bg-slate-100 p-1 shadow-sm shadow-slate-900/5">
        {sections.map((section) => (
          <button
            key={section}
            type="button"
            onClick={() => onChange(section)}
            className={[
              'h-10 cursor-pointer rounded-[8px] px-4 text-sm font-bold transition focus:outline-none focus:ring-4 focus:ring-teal-100',
              activeSection === section ? 'bg-obsidian-400 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800',
            ].join(' ')}
          >
            {section}
          </button>
        ))}
      </div>
    </div>
  );
}

function ProfileSection({ profile, user }) {
  const displayName = profile?.full_name || user?.user_metadata?.full_name || 'Not set';
  const useCase = profile?.career_status === 'active' ? 'both' : 'jobs';
  const company = profile?.current_company || profile?.company || 'Not set';
  const whatsapp = profile?.whatsapp_number || profile?.phone || 'Not set';

  return (
    <SettingsCard id="profile" eyebrow="Profile" title="Account profile">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Display name" htmlFor="display-name">
          <input id="display-name" type="text" value={displayName} readOnly className={inputClassName} />
        </Field>
        <Field label="Primary use case" htmlFor="primary-use-case">
          <select id="primary-use-case" value={useCase} disabled className={inputClassName}>
            <option value="jobs">Job Opportunities</option>
            <option value="experts">Expert Opportunities</option>
            <option value="both">Both</option>
          </select>
        </Field>
        <Field label="Current company" htmlFor="current-company">
          <input id="current-company" type="text" value={company} readOnly className={inputClassName} />
        </Field>
        <Field label="WhatsApp number" htmlFor="whatsapp-number">
          <div className="flex h-11 items-center justify-between gap-3 rounded-[8px] bg-slate-50 px-3 ring-1 ring-slate-200">
            <span id="whatsapp-number" className="truncate text-sm font-bold text-slate-700">{whatsapp}</span>
            <span className="shrink-0 text-xs font-bold text-slate-400">Managed</span>
          </div>
        </Field>
      </div>
    </SettingsCard>
  );
}

function PaymentsSection({ profile }) {
  const connected = Boolean(profile?.stripe_account_id);
  const enabled = Boolean(profile?.stripe_payouts_enabled);
  return (
    <SettingsCard id="payments" eyebrow="Payments" title="Payout details">
      <div className="rounded-xl bg-slate-50 p-4">
        <p className="text-sm font-bold text-obsidian-400">{connected ? 'Stripe connected' : 'Payout account not connected'}</p>
        <p className="mt-1 text-sm font-semibold text-slate-500">{enabled ? 'Payouts enabled' : 'Finish payout setup to withdraw interview earnings'}</p>
      </div>
    </SettingsCard>
  );
}

function SubscriptionSection({ profile }) {
  const active = profile?.career_status === 'active';
  return (
    <SettingsCard id="subscription" eyebrow="Subscription" title="Plan status">
      <div className="flex flex-col gap-3 rounded-xl bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-emerald-800">Career agency access</p>
          <p className="mt-1 text-sm font-semibold text-emerald-700">{active ? 'Active participant plan' : 'Inactive participant plan'}</p>
        </div>
        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700 shadow-sm">{active ? 'Active' : 'Inactive'}</span>
      </div>
    </SettingsCard>
  );
}

function IntegrationsSection() {
  return (
    <SettingsCard id="integrations" eyebrow="Integrations" title="Email connection">
      <div className="flex flex-col gap-4 rounded-xl bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-bold text-obsidian-400">Connect your email</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">Receive copies of employer correspondence from your proxy inbox</p>
        </div>
        <button type="button" className="h-11 cursor-pointer rounded-[8px] bg-obsidian-400 px-4 text-sm font-bold text-white shadow-lg shadow-slate-900/10 transition hover:-translate-y-0.5 hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-teal-100">
          Connect
        </button>
      </div>
    </SettingsCard>
  );
}

function SkillsSection({ profile }) {
  const skills = Array.isArray(profile?.areas_of_expertise) ? profile.areas_of_expertise : [];
  return (
    <SettingsCard id="skills" eyebrow="Skills" title="Skill profile">
      <div className="flex flex-wrap gap-2">
        {(skills.length ? skills : ['No skills added yet']).map((skill) => (
          <span key={skill} className="rounded-full bg-teal-50 px-3 py-1.5 text-xs font-bold text-teal-800">
            {skill}
          </span>
        ))}
      </div>
    </SettingsCard>
  );
}

function PreferencesSection({ resumeMode, onResumeModeChange, coverLetterMode, onCoverLetterModeChange, notifications, onToggleNotification }) {
  return (
    <div className="space-y-6">
      <SettingsCard eyebrow="Resume optimization" title="Resume optimization">
        <RadioGroup name="resume-mode" options={resumeOptions} value={resumeMode} onChange={onResumeModeChange} />
      </SettingsCard>

      <SettingsCard eyebrow="Cover letters" title="Cover letter generation">
        <RadioGroup name="cover-letter-mode" options={coverLetterOptions} value={coverLetterMode} onChange={onCoverLetterModeChange} />
      </SettingsCard>

      <SettingsCard eyebrow="Notifications" title="Notifications">
        <div className="space-y-3">
          <ToggleRow label="WhatsApp job alerts" enabled={notifications.whatsapp} onToggle={() => onToggleNotification('whatsapp')} />
          <ToggleRow label="Email interview invites" enabled={notifications.email} onToggle={() => onToggleNotification('email')} />
          <ToggleRow label="Application status updates" enabled={notifications.application} onToggle={() => onToggleNotification('application')} />
        </div>
      </SettingsCard>
    </div>
  );
}

function DangerZone() {
  return (
    <section className="rounded-xl border border-coral-100 bg-white p-5 shadow-sm shadow-slate-900/5">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-coral-500">Danger zone</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" className="h-11 cursor-pointer rounded-[8px] border border-coral-200 bg-white px-4 text-sm font-bold text-coral-600 transition hover:-translate-y-0.5 hover:bg-coral-50 focus:outline-none focus:ring-4 focus:ring-red-100">
          Pause account
        </button>
        <a href="/account/delete" className="text-sm font-bold text-coral-600 underline decoration-coral-200 underline-offset-4 hover:text-coral-700 focus:outline-none focus:ring-4 focus:ring-red-100">
          Delete account
        </a>
      </div>
    </section>
  );
}

function SettingsCard({ id, eyebrow, title, children }) {
  return (
    <section id={id} className="rounded-xl bg-white p-5 shadow-sm shadow-slate-900/5">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">{eyebrow}</p>
      <h2 className="mt-1 text-xl font-bold text-obsidian-400">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {options.map((option) => (
        <label key={option.value} className={['cursor-pointer rounded-xl p-4 ring-1 transition', value === option.value ? 'bg-teal-50 ring-teal-200' : 'bg-slate-50 ring-slate-200 hover:bg-white'].join(' ')}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-base font-bold text-obsidian-400">{option.label}</span>
            <input type="radio" name={name} value={option.value} checked={value === option.value} onChange={() => onChange(option.value)} className="h-4 w-4 accent-teal-600" />
          </div>
          <p className="mt-2 text-sm leading-5 text-slate-500">{option.description}</p>
        </label>
      ))}
    </div>
  );
}

function ToggleRow({ label, enabled, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-4">
      <span className="text-sm font-bold text-obsidian-400">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        className={['relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition focus:outline-none focus:ring-4 focus:ring-teal-100', enabled ? 'bg-teal-600' : 'bg-slate-300'].join(' ')}
      >
        <span className={['absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition', enabled ? 'left-6' : 'left-1'].join(' ')} />
      </button>
    </div>
  );
}

const inputClassName = 'h-11 w-full rounded-[8px] bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none ring-1 ring-slate-200 transition focus:bg-white focus:ring-4 focus:ring-teal-100';

function SettingsSkeleton() {
  return (
    <section className="space-y-6 animate-pulse">
      <div className="h-36 rounded-2xl bg-slate-200" />
      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <div className="hidden h-64 rounded-xl bg-slate-200 lg:block" />
        <div className="space-y-6">
          <div className="h-48 rounded-xl bg-slate-200" />
          <div className="h-40 rounded-xl bg-slate-200" />
          <div className="h-56 rounded-xl bg-slate-200" />
        </div>
      </div>
    </section>
  );
}
